export type CampaignColumnLifecycleStatus = 'PREVIEW' | 'APPROVED'

export interface CampaignColumnProgress {
  total_contacts: number
  computed: number
  valid_values: number
  failed: number
  edited: number
}

export interface CampaignColumn {
  id: number
  campaign: number
  name: string
  merge_variable_slug: string
  engine_type: string
  prompt: string
  version: number
  output_schema: Record<string, unknown>
  created_by: number
  created_at: string
  updated_at: string
  status: CampaignColumnLifecycleStatus
  settings: Record<string, unknown>
  progress: CampaignColumnProgress
}

export interface CampaignColumnValue {
  id: number
  column: number
  contact: number
  value: { value: string } | null
  is_user_edited: boolean
  error: string | null
  version_computed: number | null
  computed_at: string | null
}

// 'empty' covers both "no row yet" and "compute crashed before writing" — the
// renderer disambiguates with `columnStatus`/`showLoadingForEmpty` to decide
// whether to show a spinner or an em-dash. There is no separate 'pending'
// state because compute writes its row only on completion (success or error).
export type CellState = 'computed' | 'edited' | 'failed' | 'empty'

// Anchored patterns mirroring server/campaign_columns/engines/llm_result.py
// _NON_ANSWER_PATTERNS. Substring matching here would false-positive on
// dishes like "Pancake stack" (matches "can't ") or districts named with
// "insufficient" — anchor on word boundaries so only refusal-style text
// gets dropped.
const NON_ANSWER_PATTERNS: readonly RegExp[] = [
  /^(i('m| am)? )?unable to\b/,
  /\bi\s+can(?:'|no)t\b/,
  /\bcannot\s+(?:propose|provide|determine|generate|find)\b/,
  /\bneed (?:product information|a manufacturer product)\b/,
  /\bmanufacturer product to proceed\b/,
  /\bno products (?:provided|configured)\b/,
  /\b(?:not enough|insufficient) information\b/,
]

export function isNonAnswerValue(text: string) {
  if (!text) return false
  const normalized = text.toLowerCase()
  return NON_ANSWER_PATTERNS.some((pattern) => pattern.test(normalized))
}

export function deriveCellState(value?: CampaignColumnValue): CellState {
  if (!value) return 'empty'
  if (value.is_user_edited) return 'edited'
  if (value.error && value.error !== 'output_truncated') return 'failed'
  if (value.value?.value && !isNonAnswerValue(value.value.value)) {
    return 'computed'
  }
  return 'empty'
}

export interface DefineColumnRequest {
  agent_type: string
  name: string
  settings?: Record<string, unknown>
  // Custom-only:
  merge_variable_slug?: string
  prompt?: string
}

export interface UpdateColumnRequest {
  name?: string
  prompt?: string
}

export interface RecomputeRequest {
  include_edited?: boolean
}

export interface EditValueRequest {
  new_value: string
}

export interface TestPromptRequest {
  campaign_id: number
  prompt: string
  contact_id?: number
}

export interface TestPromptResponse {
  value: string | null
  error: string | null
}

export interface AgentPreflightRequirement {
  key: string
  label: string
  missing_count: number
  message: string
}

export interface AgentPreflight {
  agent_type: string
  total_contacts: number
  ready_contacts: number
  missing_contacts: number
  requirements: AgentPreflightRequirement[]
}
